import type { AtsEntityTypeInfo } from "@/types/ats-explorer-api"

export interface EntityGroup {
  /** The vendor's group name, or null for entities the vendor leaves ungrouped */
  name: string | null
  entities: AtsEntityTypeInfo[]
}

/** Map key for the ungrouped bucket; the group itself carries a null name so the UI can label it */
const UNGROUPED = "__ungrouped__"

/** Case-insensitive match on display name or vendor name */
export function matchesEntityQuery(entity: AtsEntityTypeInfo, query: string): boolean {
  const q = query.trim().toLowerCase()
  return q === "" || entity.displayName.toLowerCase().includes(q) || entity.name.toLowerCase().includes(q)
}

/**
 * Groups entity types for browsing, preserving the server's order (curated core
 * entities first, then discovered ones by group). Ungrouped vendors collapse to a
 * single group, so callers render a flat list when only one comes back.
 */
export function groupEntityTypes(entities: AtsEntityTypeInfo[], query = ""): EntityGroup[] {
  const groups = new Map<string, AtsEntityTypeInfo[]>()

  for (const entity of entities) {
    if (!matchesEntityQuery(entity, query)) continue
    const name = entity.group ?? UNGROUPED
    const members = groups.get(name)
    if (members) {
      members.push(entity)
    } else {
      groups.set(name, [entity])
    }
  }

  return Array.from(groups, ([name, members]) => ({ name: name === UNGROUPED ? null : name, entities: members }))
}
